import {
  addAudioTrack,
  addGuide,
  addLayer,
  clearGuides,
  duplicateLayer,
  getState,
  makeImageLayer,
  makeShapeLayer,
  makeTextLayer,
  makeVideoLayer,
  removeLayer,
  reorderLayer,
  setPenColor,
  setPenMode,
  setPenShape,
  setPenWidth,
  setSelectedId,
  setTool,
  subscribe,
  uid,
  updateLayer,
} from '../store/project';
import { extractVideoId } from '../audio/youtube';
import { openContextMenu } from '../ui/ContextMenu';
import { icon, type IconName } from '../ui/icons';
import { buildLayerMenu } from '../ui/menus';
import type { AudioTrack } from '../types';

const KIND_LABEL: Record<string, string> = {
  text: 'T',
  image: 'IMG',
  video: 'VID',
  shape: '◼',
};

export class LayerPanel {
  private root: HTMLElement;
  private list!: HTMLElement;
  private brushPopover: HTMLElement | null = null;
  private brushDocHandler: ((e: MouseEvent) => void) | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.build();
    // brush popover code is parked until we surface a dedicated entry point
    void this.toggleBrushPopover;
    // renderList is not subscribed — the layer list lives in the timeline side
    // panel now. Keep the method around for potential future inline usage.
    void this.renderList;

    // Width-tier classes so we can show/hide labels reliably without relying
    // on @container queries (which weren't applying for some users).
    const ro = new ResizeObserver(() => {
      const w = this.root.clientWidth;
      this.root.classList.toggle('layers-md', w >= 90);
      this.root.classList.toggle('layers-lg', w >= 140);
    });
    ro.observe(this.root);
  }

  private build(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);

    // ─── Section: 컨트롤 (cursor / hand / lasso / guide) ───
    const toolRow = makeSection('컨트롤', 'tool-row', 'tools');
    const tools: { name: 'select' | 'pan' | 'lasso'; icon: IconName; title: string; label: string }[] = [
      { name: 'select', icon: 'cursor', title: '선택 (V)', label: '선택' },
      { name: 'pan', icon: 'hand', title: '손바닥 — 화면 이동 (H)', label: '손바닥' },
      { name: 'lasso', icon: 'lasso', title: '박스 다중 선택 (M)', label: '다중 선택' },
    ];
    const toolButtons: Record<string, HTMLButtonElement> = {};
    for (const t of tools) {
      const btn = this.makeIconBtn(t.icon, t.title, t.label, () => {
        setTool(t.name);
      });
      toolButtons[t.name] = btn;
      toolRow.body.appendChild(btn);
    }
    // Guide adder — Photoshop-style. Opens a small popover with vertical /
    // horizontal choices; either choice drops a guide at the canvas centre.
    toolRow.body.appendChild(
      this.makeIconBtn('guide', '가이드 추가', '가이드', (btn) => this.toggleGuidePopover(btn)),
    );
    subscribe((s) => {
      for (const name of Object.keys(toolButtons)) {
        toolButtons[name]?.classList.toggle('active', s.tool === name);
      }
    });

    // ─── Section: 오브젝트 (text / shape / image / video / audio / bg / pen) ───
    const addRow = makeSection('오브젝트', 'add-row', 'objects');
    addRow.body.appendChild(this.makeIconBtn('kind-text', '텍스트 추가', '텍스트', () => addLayer(makeTextLayer())));
    addRow.body.appendChild(this.makeIconBtn('kind-shape', '도형 추가', '도형', () => addLayer(makeShapeLayer())));
    addRow.body.appendChild(this.makeIconBtn('kind-image', '이미지 추가', '이미지', (btn) => this.toggleMediaPopover(btn, 'image')));
    addRow.body.appendChild(this.makeIconBtn('kind-video', '동영상 추가', '동영상', (btn) => this.toggleMediaPopover(btn, 'video')));
    addRow.body.appendChild(this.makeIconBtn('audio', '사운드 추가', '사운드', (btn) => this.toggleSoundPopover(btn)));
    addRow.body.appendChild(this.makeIconBtn('background', '배경 변경', '배경', () => {
      const w = window as unknown as { _bgPopout?: { toggle: () => void } };
      w._bgPopout?.toggle();
    }));
    // Pen / brush — moved here from 컨트롤 by request. Activates draw mode
    // and opens the brush popover, same flow as before.
    const drawBtn = this.makeIconBtn('kind-drawing', '펜 그리기 (B)', '펜', (btn) => {
      setTool('draw');
      this.toggleBrushPopover(btn);
    });
    toolButtons['draw'] = drawBtn;
    addRow.body.appendChild(drawBtn);

    // Append in order
    this.root.appendChild(toolRow.section);
    this.root.appendChild(addRow.section);

    // Drag-to-reorder is wired LATER, by main.ts, after AI / scripts sections
    // are also appended — otherwise those external sections wouldn't get
    // their drag listeners.

    // Vestigial list element kept off-DOM so legacy renderList() still works
    // without writing to a missing reference. Not appended to root.
    this.list = document.createElement('div');
    this.list.className = 'list';
  }

  private makeIconBtn(
    iconName: IconName,
    title: string,
    label: string,
    onClick: (btn: HTMLButtonElement) => void,
  ): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'add-icon';
    b.title = title;
    const ic = icon(iconName, { size: 18 });
    b.appendChild(ic);
    const lab = document.createElement('span');
    lab.className = 'add-icon-label';
    lab.textContent = label;
    b.appendChild(lab);
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick(b);
    });
    return b;
  }


  private toggleBrushPopover(anchor: HTMLElement): void {
    if (this.brushPopover) {
      this.closeBrushPopover();
      return;
    }
    const pop = document.createElement('div');
    pop.className = 'popover brush-popover';
    pop.addEventListener('mousedown', (e) => e.stopPropagation());
    pop.addEventListener('click', (e) => e.stopPropagation());

    // If a drawing layer is selected, edit its brush override; otherwise
    // edit the global pen settings (used as defaults for new strokes).
    const getActive = (): { kind: 'layer'; id: string; brush: NonNullable<import('../types').DrawingLayer['brush']> } | { kind: 'global' } => {
      const s = getState();
      const sel = s.selectedId ? s.project.layers.find((l) => l.id === s.selectedId) : null;
      if (sel && sel.kind === 'drawing') {
        return { kind: 'layer', id: sel.id, brush: sel.brush ?? {} };
      }
      return { kind: 'global' };
    };

    const setBrushColor = (c: string): void => {
      const a = getActive();
      if (a.kind === 'layer') updateLayer(a.id, { brush: { ...a.brush, color: c } });
      else setPenColor(c);
    };
    const setBrushWidth = (w: number): void => {
      const a = getActive();
      if (a.kind === 'layer') updateLayer(a.id, { brush: { ...a.brush, width: w } });
      else setPenWidth(w);
    };
    const setBrushShape = (sh: 'round' | 'butt'): void => {
      const a = getActive();
      if (a.kind === 'layer') updateLayer(a.id, { brush: { ...a.brush, shape: sh } });
      else setPenShape(sh);
    };

    const readColor = (): string => {
      const a = getActive();
      if (a.kind === 'layer' && a.brush.color) return a.brush.color;
      return getState().penColor;
    };
    const readWidth = (): number => {
      const a = getActive();
      if (a.kind === 'layer' && a.brush.width != null) return a.brush.width;
      return getState().penWidth;
    };
    const readShape = (): 'round' | 'butt' => {
      const a = getActive();
      if (a.kind === 'layer' && a.brush.shape) return a.brush.shape;
      return getState().penShape;
    };

    const title = document.createElement('div');
    title.className = 'popover-title';
    const updateTitle = (): void => {
      const a = getActive();
      title.textContent = a.kind === 'layer' ? '브러시 (선택된 그림 편집)' : '브러시 (기본값)';
    };
    updateTitle();
    pop.appendChild(title);

    // pen / eraser
    const modeRow = document.createElement('div');
    modeRow.className = 'popover-row';
    for (const m of ['pen', 'eraser'] as const) {
      const b = document.createElement('button');
      b.className = 'btn ghost';
      b.textContent = m === 'pen' ? '✏️ 펜' : '🧽 지우개';
      b.dataset.mode = m;
      b.addEventListener('click', () => {
        setPenMode(m);
        modeRow.querySelectorAll<HTMLElement>('button').forEach((x) =>
          x.classList.toggle('active', x.dataset.mode === m),
        );
      });
      if (getState().penMode === m) b.classList.add('active');
      modeRow.appendChild(b);
    }
    pop.appendChild(this.popField('모드', modeRow));

    // color
    const color = document.createElement('input');
    color.type = 'color';
    color.value = readColor();
    color.style.width = '100%';
    color.style.height = '32px';
    color.addEventListener('input', () => setBrushColor(color.value));
    pop.appendChild(this.popField('색상', color));

    // width
    const widthWrap = document.createElement('div');
    widthWrap.style.display = 'grid';
    widthWrap.style.gridTemplateColumns = '1fr 48px';
    widthWrap.style.gap = '8px';
    const widthRange = document.createElement('input');
    widthRange.type = 'range';
    widthRange.min = '1';
    widthRange.max = '60';
    widthRange.value = String(readWidth());
    const widthNum = document.createElement('input');
    widthNum.type = 'number';
    widthNum.min = '1';
    widthNum.max = '80';
    widthNum.value = String(readWidth());
    const sync = (v: number): void => {
      setBrushWidth(v);
      widthRange.value = String(v);
      widthNum.value = String(v);
    };
    widthRange.addEventListener('input', () => sync(parseInt(widthRange.value, 10)));
    widthNum.addEventListener('input', () => {
      const v = parseInt(widthNum.value, 10);
      if (!Number.isNaN(v)) sync(v);
    });
    widthWrap.append(widthRange, widthNum);
    pop.appendChild(this.popField('굵기', widthWrap));

    // shape: round / butt
    const shapeRow = document.createElement('div');
    shapeRow.className = 'popover-row';
    for (const sh of ['round', 'butt'] as const) {
      const b = document.createElement('button');
      b.className = 'btn ghost';
      b.textContent = sh === 'round' ? '● 라운드' : '■ 스퀘어';
      b.dataset.shape = sh;
      b.addEventListener('click', () => {
        setBrushShape(sh);
        shapeRow.querySelectorAll<HTMLElement>('button').forEach((x) =>
          x.classList.toggle('active', x.dataset.shape === sh),
        );
      });
      if (readShape() === sh) b.classList.add('active');
      shapeRow.appendChild(b);
    }
    pop.appendChild(this.popField('모양', shapeRow));

    // Re-render reflected state when the store changes (selection switch etc.)
    const unsub = subscribe(() => {
      updateTitle();
      const c = readColor();
      if (color.value.toLowerCase() !== c.toLowerCase()) color.value = c;
      const w = String(readWidth());
      if (widthRange.value !== w) widthRange.value = w;
      if (widthNum.value !== w) widthNum.value = w;
      shapeRow.querySelectorAll<HTMLElement>('button').forEach((x) =>
        x.classList.toggle('active', x.dataset.shape === readShape()),
      );
    });
    pop.dataset.unsubAttached = '1';
    (pop as unknown as { _unsub?: () => void })._unsub = unsub;

    // exit
    const exit = document.createElement('button');
    exit.className = 'btn ghost popover-exit';
    exit.textContent = '✕ 그리기 끝내기 (Esc)';
    exit.addEventListener('click', () => {
      setTool('select');
      this.closeBrushPopover();
    });
    pop.appendChild(exit);

    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.left = `${Math.max(8, r.left)}px`;
    pop.style.top = `${r.bottom + 6}px`;

    this.brushPopover = pop;

    // Close on outside click
    this.brushDocHandler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.contains(t) || anchor.contains(t)) return;
      this.closeBrushPopover();
    };
    setTimeout(() => {
      if (this.brushDocHandler) document.addEventListener('mousedown', this.brushDocHandler);
    }, 0);
  }

  private soundPopover: HTMLElement | null = null;
  private soundDocHandler: ((e: MouseEvent) => void) | null = null;
  private mediaPopover: HTMLElement | null = null;
  private mediaDocHandler: ((e: MouseEvent) => void) | null = null;
  private guidePopover: HTMLElement | null = null;
  private guideDocHandler: ((e: MouseEvent) => void) | null = null;

  private toggleGuidePopover(anchor: HTMLElement): void {
    if (this.guidePopover) {
      this.closeGuidePopover();
      return;
    }
    const pop = document.createElement('div');
    pop.className = 'popover guide-popover';
    pop.addEventListener('mousedown', (e) => e.stopPropagation());
    pop.addEventListener('click', (e) => e.stopPropagation());

    const title = document.createElement('div');
    title.className = 'popover-title';
    title.textContent = '+ 가이드 추가';
    pop.appendChild(title);

    const note = document.createElement('div');
    note.style.fontSize = '11px';
    note.style.color = 'var(--ink-dim)';
    note.textContent = '가이드는 캔버스 중앙에 추가돼요. 드래그로 이동, 더블클릭으로 삭제.';
    pop.appendChild(note);

    const row = document.createElement('div');
    row.className = 'popover-row';
    const v = document.createElement('button');
    v.type = 'button';
    v.className = 'btn';
    v.textContent = '┃ 세로';
    v.addEventListener('click', () => {
      addGuide('vertical');
      this.closeGuidePopover();
    });
    const h = document.createElement('button');
    h.type = 'button';
    h.className = 'btn';
    h.textContent = '━ 가로';
    h.addEventListener('click', () => {
      addGuide('horizontal');
      this.closeGuidePopover();
    });
    row.append(v, h);
    pop.appendChild(row);

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'btn ghost popover-exit';
    clearBtn.textContent = '모두 지우기';
    clearBtn.addEventListener('click', () => {
      clearGuides();
      this.closeGuidePopover();
    });
    pop.appendChild(clearBtn);

    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.left = `${Math.max(8, r.left)}px`;
    pop.style.top = `${r.bottom + 6}px`;

    this.guidePopover = pop;
    this.guideDocHandler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.contains(t) || anchor.contains(t)) return;
      this.closeGuidePopover();
    };
    setTimeout(() => {
      if (this.guideDocHandler) document.addEventListener('mousedown', this.guideDocHandler);
    }, 0);
  }

  private closeGuidePopover(): void {
    if (this.guideDocHandler) document.removeEventListener('mousedown', this.guideDocHandler);
    this.guideDocHandler = null;
    this.guidePopover?.remove();
    this.guidePopover = null;
  }

  private toggleMediaPopover(anchor: HTMLElement, kind: 'image' | 'video'): void {
    if (this.mediaPopover) {
      this.closeMediaPopover();
      return;
    }
    const pop = document.createElement('div');
    pop.className = 'popover media-popover';
    pop.addEventListener('mousedown', (e) => e.stopPropagation());
    pop.addEventListener('click', (e) => e.stopPropagation());

    const title = document.createElement('div');
    title.className = 'popover-title';
    title.textContent = kind === 'image' ? '+ 이미지' : '+ 동영상';
    pop.appendChild(title);

    const tabRow = document.createElement('div');
    tabRow.className = 'popover-row';
    let activeTab: 'url' | 'upload' = 'url';
    const tabButtons: HTMLButtonElement[] = [];
    for (const t of ['url', 'upload'] as const) {
      const b = document.createElement('button');
      b.className = 'btn ghost';
      b.textContent = t === 'url' ? 'URL' : '업로드';
      b.dataset.tab = t;
      b.addEventListener('click', () => {
        activeTab = t;
        tabButtons.forEach((x) => x.classList.toggle('active', x.dataset.tab === t));
        renderInputs();
      });
      tabButtons.push(b);
      tabRow.appendChild(b);
    }
    tabButtons[0]?.classList.add('active');
    pop.appendChild(this.popField('소스', tabRow));

    const inputArea = document.createElement('div');
    inputArea.style.display = 'flex';
    inputArea.style.flexDirection = 'column';
    inputArea.style.gap = '6px';
    pop.appendChild(inputArea);

    let urlInput: HTMLInputElement | null = null;
    let fileSrc = '';
    let fileName = '';

    const renderInputs = (): void => {
      inputArea.replaceChildren();
      if (activeTab === 'url') {
        urlInput = document.createElement('input');
        urlInput.type = 'text';
        urlInput.placeholder =
          kind === 'image'
            ? 'https://example.com/image.jpg'
            : 'https://example.com/video.mp4';
        inputArea.appendChild(urlInput);
        const note = document.createElement('div');
        note.style.fontSize = '11px';
        note.style.color = 'var(--ink-dim)';
        note.textContent =
          kind === 'image'
            ? '* 배경으로 쓰려면 추가한 뒤 캔버스 크기로 늘리면 돼요.'
            : '* 외부 URL은 CORS가 허용된 호스트만 재생 가능.';
        inputArea.appendChild(note);
      } else {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = kind === 'image' ? 'image/*' : 'video/*';
        const info = document.createElement('div');
        info.style.fontSize = '11px';
        info.style.color = 'var(--ink-dim)';
        fileInput.addEventListener('change', () => {
          const f = fileInput.files?.[0];
          if (!f) return;
          info.textContent = `${f.name} · ${Math.round(f.size / 1024)} KB · 로딩…`;
          const reader = new FileReader();
          reader.onload = () => {
            fileSrc = reader.result as string;
            fileName = f.name;
            info.textContent = `${f.name} · 준비됨`;
          };
          reader.readAsDataURL(f);
        });
        inputArea.append(fileInput, info);
      }
    };
    renderInputs();

    const status = document.createElement('div');
    status.style.fontSize = '11px';
    status.style.color = 'var(--ink-dim)';

    const addBtn = document.createElement('button');
    addBtn.className = 'btn primary';
    addBtn.textContent = '추가';
    addBtn.addEventListener('click', () => {
      let src = '';
      let name = kind === 'image' ? '이미지' : '동영상';
      if (activeTab === 'url') {
        src = urlInput?.value.trim() ?? '';
        if (!src) {
          status.textContent = '⚠ URL을 입력하세요';
          status.style.color = 'var(--accent)';
          return;
        }
      } else {
        if (!fileSrc) {
          status.textContent = '⚠ 파일을 선택하세요';
          status.style.color = 'var(--accent)';
          return;
        }
        src = fileSrc;
        if (fileName) name = fileName;
      }
      if (kind === 'image') addLayer(makeImageLayer(src, name));
      else addLayer(makeVideoLayer(src, name));
      this.closeMediaPopover();
    });
    pop.append(status, addBtn);

    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.left = `${Math.max(8, r.left)}px`;
    pop.style.top = `${r.bottom + 6}px`;
    pop.style.width = '300px';

    this.mediaPopover = pop;
    this.mediaDocHandler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.contains(t) || anchor.contains(t)) return;
      this.closeMediaPopover();
    };
    setTimeout(() => {
      if (this.mediaDocHandler) document.addEventListener('mousedown', this.mediaDocHandler);
    }, 0);
  }

  private closeMediaPopover(): void {
    if (this.mediaPopover) {
      this.mediaPopover.remove();
      this.mediaPopover = null;
    }
    if (this.mediaDocHandler) {
      document.removeEventListener('mousedown', this.mediaDocHandler);
      this.mediaDocHandler = null;
    }
  }

  private toggleSoundPopover(anchor: HTMLElement): void {
    if (this.soundPopover) {
      this.closeSoundPopover();
      return;
    }
    const pop = document.createElement('div');
    pop.className = 'popover sound-popover';
    pop.addEventListener('mousedown', (e) => e.stopPropagation());
    pop.addEventListener('click', (e) => e.stopPropagation());

    const title = document.createElement('div');
    title.className = 'popover-title';
    title.textContent = '+ 사운드 추가';
    pop.appendChild(title);

    const kindRow = document.createElement('div');
    kindRow.className = 'popover-row';
    let activeKind: 'youtube' | 'file' | 'soundcloud' | 'spotify' = 'youtube';
    const kinds: Array<['youtube' | 'file' | 'soundcloud' | 'spotify', string]> = [
      ['youtube', 'YouTube'],
      ['file', '파일'],
      ['soundcloud', 'SoundCloud'],
      ['spotify', 'Spotify'],
    ];
    const kindButtons: HTMLButtonElement[] = [];
    for (const [k, label] of kinds) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'btn ghost';
      b.textContent = label;
      b.dataset.kind = k;
      b.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        activeKind = k;
        kindButtons.forEach((x) => x.classList.toggle('active', x.dataset.kind === k));
        renderInputs();
      });
      kindButtons.push(b);
      kindRow.appendChild(b);
    }
    kindButtons[0]?.classList.add('active');
    pop.appendChild(this.popField('종류', kindRow));

    const inputArea = document.createElement('div');
    inputArea.style.display = 'flex';
    inputArea.style.flexDirection = 'column';
    inputArea.style.gap = '6px';
    pop.appendChild(inputArea);

    let urlInput: HTMLInputElement | null = null;
    let fileSrc = '';
    let fileName = '';

    const renderInputs = (): void => {
      inputArea.replaceChildren();
      if (activeKind === 'file') {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'audio/*';
        const status = document.createElement('div');
        status.style.fontSize = '11px';
        status.style.color = 'var(--ink-dim)';
        fileInput.addEventListener('change', () => {
          const f = fileInput.files?.[0];
          if (!f) return;
          status.textContent = `${f.name} · ${Math.round(f.size / 1024)} KB · 로딩…`;
          const reader = new FileReader();
          reader.onload = () => {
            fileSrc = reader.result as string;
            fileName = f.name;
            status.textContent = `${f.name} · 준비됨`;
          };
          reader.readAsDataURL(f);
        });
        inputArea.append(fileInput, status);
      } else {
        urlInput = document.createElement('input');
        urlInput.type = 'text';
        urlInput.placeholder =
          activeKind === 'youtube'
            ? 'https://www.youtube.com/watch?v=...'
            : activeKind === 'soundcloud'
              ? 'https://soundcloud.com/...'
              : 'https://open.spotify.com/track/...';
        inputArea.appendChild(urlInput);
        if (activeKind === 'spotify' || activeKind === 'soundcloud') {
          const note = document.createElement('div');
          note.style.fontSize = '11px';
          note.style.color = 'var(--warn)';
          note.style.lineHeight = '1.5';
          // Be explicit: this build saves the track info but does not actually
          // play Spotify or SoundCloud audio yet. Avoid surprising the user
          // when they hit Play and hear nothing.
          note.textContent =
            activeKind === 'spotify'
              ? '⚠ 현재 Spotify는 재생되지 않아요 (정보만 저장됩니다). 실제 재생은 다음 업데이트.'
              : '⚠ 현재 SoundCloud는 재생되지 않아요 (정보만 저장됩니다). 실제 재생은 다음 업데이트.';
          inputArea.appendChild(note);
        }
      }
    };
    renderInputs();

    // common: songStart / songEnd
    const startInput = mkSmallInput('시작 ms', '0');
    const endInput = mkSmallInput('끝 ms', '30000');
    const timing = document.createElement('div');
    timing.style.display = 'grid';
    timing.style.gridTemplateColumns = '1fr 1fr';
    timing.style.gap = '6px';
    timing.append(startInput, endInput);
    pop.appendChild(this.popField('범위', timing));

    const status = document.createElement('div');
    status.style.fontSize = '11px';
    status.style.color = 'var(--ink-dim)';

    const addBtn = document.createElement('button');
    addBtn.className = 'btn primary';
    addBtn.textContent = '추가';
    addBtn.addEventListener('click', () => {
      const songStart = Math.max(0, parseInt(startInput.value, 10) || 0);
      const songEnd = parseInt(endInput.value, 10) || songStart + 30000;
      if (songEnd <= songStart) {
        status.textContent = '⚠ 끝이 시작보다 커야 합니다';
        status.style.color = 'var(--accent)';
        return;
      }
      let track: AudioTrack | null = null;
      if (activeKind === 'youtube') {
        const id = extractVideoId(urlInput?.value ?? '');
        if (!id) {
          status.textContent = '⚠ YouTube URL 인식 못 함';
          status.style.color = 'var(--accent)';
          return;
        }
        track = { id: uid('a'), kind: 'youtube', videoId: id, songStart, songEnd, volume: 0.6 };
      } else if (activeKind === 'file') {
        if (!fileSrc) {
          status.textContent = '⚠ 파일을 선택하세요';
          status.style.color = 'var(--accent)';
          return;
        }
        track = {
          id: uid('a'),
          kind: 'file',
          src: fileSrc,
          name: fileName,
          songStart,
          songEnd,
          volume: 0.6,
        };
      } else if (activeKind === 'soundcloud') {
        const u = urlInput?.value.trim() ?? '';
        if (!u) {
          status.textContent = '⚠ URL 입력';
          status.style.color = 'var(--accent)';
          return;
        }
        track = { id: uid('a'), kind: 'soundcloud', url: u, songStart, songEnd, volume: 0.6 };
      } else {
        const u = urlInput?.value.trim() ?? '';
        if (!u) {
          status.textContent = '⚠ URL 입력';
          status.style.color = 'var(--accent)';
          return;
        }
        track = { id: uid('a'), kind: 'spotify', url: u, songStart, songEnd, volume: 0.6 };
      }
      addAudioTrack(track);
      status.textContent = '✅ 추가됨';
      status.style.color = 'var(--ok)';
      setTimeout(() => this.closeSoundPopover(), 600);
    });

    pop.append(status, addBtn);

    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.position = 'fixed';
    pop.style.left = `${Math.max(8, r.left)}px`;
    pop.style.top = `${r.bottom + 6}px`;
    pop.style.width = '300px';

    this.soundPopover = pop;
    this.soundDocHandler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.contains(t) || anchor.contains(t)) return;
      this.closeSoundPopover();
    };
    setTimeout(() => {
      if (this.soundDocHandler) document.addEventListener('mousedown', this.soundDocHandler);
    }, 0);
  }

  private closeSoundPopover(): void {
    if (this.soundPopover) {
      this.soundPopover.remove();
      this.soundPopover = null;
    }
    if (this.soundDocHandler) {
      document.removeEventListener('mousedown', this.soundDocHandler);
      this.soundDocHandler = null;
    }
  }

  private closeBrushPopover(): void {
    if (this.brushPopover) {
      const unsub = (this.brushPopover as unknown as { _unsub?: () => void })._unsub;
      if (typeof unsub === 'function') unsub();
      this.brushPopover.remove();
      this.brushPopover = null;
    }
    if (this.brushDocHandler) {
      document.removeEventListener('mousedown', this.brushDocHandler);
      this.brushDocHandler = null;
    }
  }

  private popField(label: string, control: HTMLElement): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'popover-field';
    const l = document.createElement('div');
    l.className = 'popover-label';
    l.textContent = label;
    wrap.append(l, control);
    return wrap;
  }

  private renderList(): void {
    const { project, selectedId } = getState();
    this.list.replaceChildren();
    // top of list = front (last in array)
    const reversed = [...project.layers].reverse();
    reversed.forEach((layer) => {
      const item = document.createElement('div');
      item.className = 'item';
      if (layer.id === selectedId) item.classList.add('selected');
      item.draggable = true;
      item.dataset.id = layer.id;

      const kind = document.createElement('span');
      kind.className = 'kind';
      kind.textContent = KIND_LABEL[layer.kind] ?? layer.kind;

      const name = document.createElement('div');
      name.className = 'name';
      name.textContent = layer.name;
      name.title = layer.name;

      const actions = document.createElement('div');
      actions.className = 'actions';
      const eye = document.createElement('button');
      eye.title = '표시/숨김';
      eye.textContent = layer.visible ? '👁' : '⏿';
      eye.addEventListener('click', (e) => {
        e.stopPropagation();
        updateLayer(layer.id, { visible: !layer.visible });
      });
      const dup = document.createElement('button');
      dup.title = '복제';
      dup.textContent = '⎘';
      dup.addEventListener('click', (e) => {
        e.stopPropagation();
        duplicateLayer(layer.id);
      });
      const del = document.createElement('button');
      del.title = '삭제';
      del.textContent = '✕';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        removeLayer(layer.id);
      });
      actions.append(eye, dup, del);

      item.append(kind, name, actions);
      item.addEventListener('click', () => setSelectedId(layer.id));
      item.addEventListener('contextmenu', (ev) => {
        setSelectedId(layer.id);
        openContextMenu(ev, buildLayerMenu(layer.id));
      });

      // drag-to-reorder (in displayed order; map back to underlying index)
      item.addEventListener('dragstart', (ev) => {
        ev.dataTransfer?.setData('text/plain', layer.id);
      });
      item.addEventListener('dragover', (ev) => ev.preventDefault());
      item.addEventListener('drop', (ev) => {
        ev.preventDefault();
        const draggedId = ev.dataTransfer?.getData('text/plain');
        if (!draggedId) return;
        const targetIdx = project.layers.findIndex((l) => l.id === layer.id);
        // dropping on a row places dragged item at that row's index (underlying)
        reorderLayer(draggedId, targetIdx);
      });

      this.list.appendChild(item);
    });
  }
}

function mkSmallInput(placeholder: string, defaultValue = ''): HTMLInputElement {
  const i = document.createElement('input');
  i.type = 'text';
  i.placeholder = placeholder;
  i.value = defaultValue;
  return i;
}

/** Build a section with a tiny title (visible text scales with column width
    via CSS container queries) plus a body container the caller can fill. */
function makeSection(
  title: string,
  bodyClass: string,
  sectionId: string,
): { section: HTMLElement; body: HTMLElement } {
  const section = document.createElement('div');
  section.className = 'side-section';
  section.dataset.sectionId = sectionId;
  section.draggable = true;

  const head = document.createElement('div');
  head.className = 'side-section-title';
  head.textContent = title;
  section.appendChild(head);

  const body = document.createElement('div');
  body.className = bodyClass;
  section.appendChild(body);

  return { section, body };
}

const SECTION_ORDER_KEY = 'flash:layout:sideSectionOrder';

function loadSectionOrder(): string[] | null {
  try {
    const raw = localStorage.getItem(SECTION_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return null;
  }
}
function saveSectionOrder(ids: string[]): void {
  try {
    localStorage.setItem(SECTION_ORDER_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota */
  }
}

export function enableSectionReorder(root: HTMLElement): void {
  // Apply persisted order on render
  const saved = loadSectionOrder();
  if (saved) {
    const sections = Array.from(root.querySelectorAll<HTMLElement>('.side-section'));
    const byId = new Map(sections.map((s) => [s.dataset.sectionId ?? '', s]));
    for (const id of saved) {
      const el = byId.get(id);
      if (el) root.appendChild(el);
    }
    // append any new sections that weren't in the saved order
    for (const s of sections) {
      if (!saved.includes(s.dataset.sectionId ?? '')) root.appendChild(s);
    }
  }

  let dragId: string | null = null;
  for (const sec of root.querySelectorAll<HTMLElement>('.side-section')) {
    sec.addEventListener('dragstart', (e) => {
      dragId = sec.dataset.sectionId ?? null;
      sec.classList.add('dragging');
      e.dataTransfer?.setData('text/plain', dragId ?? '');
    });
    sec.addEventListener('dragend', () => {
      sec.classList.remove('dragging');
      dragId = null;
    });
    sec.addEventListener('dragover', (e) => {
      e.preventDefault();
      sec.classList.add('drag-over');
    });
    sec.addEventListener('dragleave', () => sec.classList.remove('drag-over'));
    sec.addEventListener('drop', (e) => {
      e.preventDefault();
      sec.classList.remove('drag-over');
      const fromId = dragId ?? e.dataTransfer?.getData('text/plain');
      if (!fromId || fromId === sec.dataset.sectionId) return;
      const fromEl = root.querySelector<HTMLElement>(`.side-section[data-section-id="${fromId}"]`);
      if (!fromEl) return;
      const sections = Array.from(root.querySelectorAll<HTMLElement>('.side-section'));
      const fromIdx = sections.indexOf(fromEl);
      const toIdx = sections.indexOf(sec);
      if (fromIdx < toIdx) {
        sec.after(fromEl);
      } else {
        sec.before(fromEl);
      }
      const ids = Array.from(root.querySelectorAll<HTMLElement>('.side-section'))
        .map((s) => s.dataset.sectionId ?? '')
        .filter(Boolean);
      saveSectionOrder(ids);
    });
  }
}
